import { type ChatAttachmentType, isChatImageType, textTypeForName } from '@af/shared';
import { useCallback, useRef, useState } from 'react';
import { toast } from 'sonner';
import { withOrgQuery } from './active-org';
import { call } from './api';
import { fileProblem, isImageType, isTextFile, planAttach } from './chat-attachment-rules';
import { IS_MOCK } from './utils';

/** Longest edge sent to the model: larger images cost more tokens and are downscaled by the provider anyway. */
const MAX_EDGE = 1568;

export interface DraftImage {
  key: string;
  name: string;
  mediaType: ChatAttachmentType;
  /** Base64 (no data: prefix). */
  data: string;
  /** Data URL for the preview (images only; empty for text files, which show as a file chip). */
  url: string;
  sizeBytes: number;
}

function toBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result).slice(String(r.result).indexOf(',') + 1));
    r.onerror = () => reject(r.error);
    r.readAsDataURL(blob);
  });
}

/** Downscale a big screenshot (≤ MAX_EDGE) in the browser; GIFs stay as they are (animation). */
async function shrink(file: File): Promise<Blob> {
  if (file.type === 'image/gif' || typeof createImageBitmap !== 'function') return file;
  try {
    const bmp = await createImageBitmap(file);
    const scale = Math.min(1, MAX_EDGE / Math.max(bmp.width, bmp.height));
    if (scale === 1 && file.size <= 1_500_000) return file;
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(bmp.width * scale);
    canvas.height = Math.round(bmp.height * scale);
    canvas.getContext('2d')?.drawImage(bmp, 0, 0, canvas.width, canvas.height);
    const encode = (type: string, q?: number) =>
      new Promise<Blob | null>((res) => canvas.toBlob(res, type, q));
    // Screenshots stay sharp as PNG; photos (or a PNG that is still huge) become JPEG.
    const png = file.type === 'image/png' ? await encode('image/png') : null;
    if (png && png.size <= 2_000_000) return png;
    return (await encode('image/jpeg', 0.88)) ?? file;
  } catch {
    return file;
  }
}

export async function prepareImage(file: File): Promise<DraftImage> {
  const problem = fileProblem(file);
  if (problem) throw new Error(problem);
  if (!isImageType(file.type) && isTextFile(file)) {
    // Text and log files go up as they are; the server checks the content.
    return {
      key: crypto.randomUUID(),
      name: file.name,
      mediaType: textTypeForName(file.name)!,
      data: await toBase64(file),
      url: '',
      sizeBytes: file.size,
    };
  }
  const blob = await shrink(file);
  const after = fileProblem({ name: file.name, type: blob.type, size: blob.size });
  if (after) throw new Error(after);
  const mediaType = (isImageType(blob.type) ? blob.type : file.type) as ChatAttachmentType;
  const data = await toBase64(blob);
  return {
    key: crypto.randomUUID(),
    name: file.name || `screenshot.${mediaType.split('/')[1]}`,
    mediaType,
    data,
    // The production CSP permits data: images; blob: object URLs would be blocked.
    url: `data:${mediaType};base64,${data}`,
    sizeBytes: blob.size,
  };
}

/** Upload the drafts to a conversation (just before sending); returns their attachment ids. */
export async function uploadDrafts(conversationId: string, drafts: DraftImage[]): Promise<string[]> {
  const ids: string[] = [];
  for (const d of drafts) {
    const a = await call('uploadAttachment', {
      params: { id: conversationId },
      body: { name: d.name, mediaType: d.mediaType, data: d.data },
    });
    ids.push(a.id);
  }
  return ids;
}

/** Image URL of a sent attachment (the mock API keeps them in memory). */
export function attachmentSrc(id: string): string {
  if (IS_MOCK)
    return (globalThis as { __afMockAttachments?: Record<string, string> }).__afMockAttachments?.[id] ?? '';
  return withOrgQuery(`/api/attachments/${encodeURIComponent(id)}`);
}

export interface AttachmentDraft {
  items: DraftImage[];
  /** Add files (picker, paste or drop); anything invalid is refused with a toast saying why. */
  add: (files: Iterable<File>) => void;
  remove: (key: string) => void;
  clear: () => void;
  preparing: boolean;
}

/** Screenshots waiting in a composer until the message is sent. */
export function useAttachmentDraft(): AttachmentDraft {
  const [items, setItems] = useState<DraftImage[]>([]);
  const [preparing, setPreparing] = useState(0);
  const pendingRef = useRef(0);
  const itemsRef = useRef(items);
  itemsRef.current = items;
  const add = useCallback((files: Iterable<File>) => {
    const list = [...files];
    if (!list.length) return;
    // Images still being prepared count towards the limit too (quick successive drops).
    const plan = planAttach(list, itemsRef.current.length + pendingRef.current);
    for (const msg of plan.errors) toast.error(msg);
    for (const i of plan.accepted) {
      pendingRef.current += 1;
      setPreparing((n) => n + 1);
      prepareImage(list[i]!)
        .then((d) => setItems((cur) => [...cur, d]))
        .catch((err: Error) => toast.error(err.message))
        .finally(() => {
          pendingRef.current -= 1;
          setPreparing((n) => n - 1);
        });
    }
  }, []);
  const remove = useCallback((key: string) => {
    setItems((cur) => {
      return cur.filter((i) => i.key !== key);
    });
  }, []);
  const clear = useCallback(() => {
    setItems([]);
  }, []);
  return { items, add, remove, clear, preparing: preparing > 0 };
}

const fileParts = (parts: Record<string, unknown>[]) =>
  parts.filter((p) => p.type === 'file' && typeof p.attachmentId === 'string');

/** `file` parts of a message that are screenshots (parts without a media type are old screenshots). */
export function imageParts(parts: Record<string, unknown>[]): { id: string; name: string }[] {
  return fileParts(parts)
    .filter((p) => typeof p.mediaType !== 'string' || isChatImageType(p.mediaType))
    .map((p) => ({ id: p.attachmentId as string, name: String(p.name ?? 'screenshot') }));
}

/** `file` parts of a message that are text/log files (shown as chips, not previews). */
export function textFileParts(
  parts: Record<string, unknown>[],
): { id: string; name: string; sizeBytes?: number }[] {
  return fileParts(parts)
    .filter((p) => typeof p.mediaType === 'string' && !isChatImageType(p.mediaType))
    .map((p) => ({
      id: p.attachmentId as string,
      name: String(p.name ?? 'file'),
      ...(typeof p.sizeBytes === 'number' ? { sizeBytes: p.sizeBytes } : {}),
    }));
}
